/**
 * Flag-aware facade selectors that delegate to v3 or v4 implementations.
 *
 * Separated from chatSelector.js to avoid circular dependency:
 * chatSelector.js ↔ legacy/chatSelector.js
 */
import { selector } from '@/lib/selectors';
import { isNewChatSelector } from '@/domain/chat/chatSelector';
import {
  modernDocumentsUnreadSelector,
  modernCompanyUnreadSelector,
  modernTotalUnreadSelector,
  modernCurrentDocumentUnreadSelector,
} from '@/domain/chat/modern/chatSelector';
import { documentsUnreadCounterV3Selector, companyUnreadCounterV3Selector, contextUnreadCountV3Selector, documentUnreadMessagesCountSelector } from '@/domain/chat/legacy/chatSelector';

// V4 returns Immutable.Map<documentId, count>, V3 returns Immutable.Set<documentId>.
// Consumers use `.has(documentId)` for presence, which works on both.
export const documentsUnreadCounterSelector = selector(
  isNewChatSelector,
  modernDocumentsUnreadSelector,
  documentsUnreadCounterV3Selector,
  (isNew, modernUnread, legacyUnread) => (isNew ? modernUnread : legacyUnread),
);

// Both return Immutable.Map<companyId, count>
export const companyUnreadCounterSelector = selector(
  isNewChatSelector,
  modernCompanyUnreadSelector,
  companyUnreadCounterV3Selector,
  (isNew, modernUnread, legacyUnread) => (isNew ? modernUnread : legacyUnread),
);

// Both return a number
export const totalUnreadCountSelector = selector(
  isNewChatSelector,
  modernTotalUnreadSelector,
  contextUnreadCountV3Selector,
  (isNew, modernCount, legacyCount) => (isNew ? modernCount : legacyCount),
);

// Document-scoped unread count — returns unread messages for the current document only.
// V3: derives from thread objects filtered by companyId + documentId.
// V4: sum of unreadMessageCount across all channels carrying this documentId, read from
//     chat.unreadDocuments (populated by the SDK collection recompute and the /unread fetch).
export const documentUnreadCountSelector = selector(
  isNewChatSelector,
  modernCurrentDocumentUnreadSelector,
  documentUnreadMessagesCountSelector,
  (isNew, modernCount, legacyCount) => (isNew ? modernCount : legacyCount),
);
